import React, { useRef } from 'react';

import PropTypes from 'prop-types';

import { URL_IMAGES_APPS, URL_IMAGES_I } from 'constants/UrlConstants';

import { useExtra, usePageMessages } from 'hooks/selectors';

import QRPopup from '../common/Popups/QRPopup';
import PromoPopup from '../popup/PromoPopup';

const MobileApp = ({ data = {}, isRequest }) => {
    const { currency_from_code, app_currency_to_code, apple_store, google_play, huawei_store } = useExtra();
    const { mobileNameUrl } = usePageMessages();
    const popupRef = useRef(null);

    const currencyFromCode = currency_from_code ?? 'eur-cny';
    const appCurrencyToCode = app_currency_to_code ?? 'link';

    const getIllustration = () => {
        if (isRequest) {
            return (
                <picture>
                    <source
                        className="lazyloading"
                        media="(min-width: 1040px)"
                        srcSet={`${URL_IMAGES_I}/apps-${appCurrencyToCode}-c.png 1x, ${URL_IMAGES_I}/apps-${appCurrencyToCode}-c@2x.png 2x`}
                    />
                    <source
                        className="lazyloading"
                        media="(min-width: 656px)"
                        srcSet={`${URL_IMAGES_I}/apps-${appCurrencyToCode}-b.png 1x, ${URL_IMAGES_I}/apps-${appCurrencyToCode}-b@2x.png 2x`}
                    />
                    <source
                        className="lazyloading"
                        srcSet={`${URL_IMAGES_I}/apps-${appCurrencyToCode}-a.png 1x, ${URL_IMAGES_I}/apps-${appCurrencyToCode}-a@2x.png 2x`}
                    />
                    <img
                        className="lazyloaded"
                        alt="Get the Paysend app today"
                        src={`${URL_IMAGES_I}/apps-${appCurrencyToCode}-a.png`}
                        loading="lazy"
                        decoding="async"
                        height="735"
                        width="640"
                    />
                </picture>
            );
        }
        return (
            <picture>
                <source
                    className="lazyloading"
                    media="(min-width: 1040px)"
                    srcSet={`${URL_IMAGES_APPS}/apps-illustration-${currencyFromCode}-c.png 1x, ${URL_IMAGES_APPS}/apps-illustration-${currencyFromCode}-c@2x.png 2x`}
                />
                <source
                    className="lazyloading"
                    media="(min-width: 656px)"
                    data-srcset={`${URL_IMAGES_APPS}/apps-illustration-${currencyFromCode}-b.png 1x, ${URL_IMAGES_APPS}/apps-illustration-${currencyFromCode}-b@2x.png 2x`}
                    srcSet={`${URL_IMAGES_APPS}/apps-illustration-${currencyFromCode}-b.png 1x, ${URL_IMAGES_APPS}/apps-illustration-${currencyFromCode}-b@2x.png 2x`}
                />
                <source
                    className="lazyloading"
                    data-srcset={`${URL_IMAGES_APPS}/apps-illustration-${currencyFromCode}-a.png 1x, ${URL_IMAGES_APPS}/apps-illustration-${currencyFromCode}-a@2x.png 2x`}
                    srcSet={`${URL_IMAGES_APPS}/apps-illustration-${currencyFromCode}-a.png 1x, ${URL_IMAGES_APPS}/apps-illustration-${currencyFromCode}-a@2x.png 2x`}
                />
                <img
                    className="lazyloaded"
                    alt="Get the Paysend app today"
                    data-src={`${URL_IMAGES_APPS}/apps-illustration-${currencyFromCode}-a.png`}
                    src={`${URL_IMAGES_APPS}/apps-illustration-${currencyFromCode}-a.png`}
                    loading="lazy"
                    decoding="async"
                    height="640"
                    width="672"
                />
            </picture>
        );
    };

    return (
        <>
            <section className="section-mobile-apps">
                <div className="box-1280">
                    <div className="inner-content">
                        <div className="lead-text max-620">
                            <h2 className="title1">{data?.title}</h2>
                            <p className="maincopy color-medium-gray">{data?.text}</p>
                        </div>
                        <figure className="mobile-apps-illustration">{getIllustration()}</figure>
                        <div className="app-links">
                            <div className="row">
                                <a aria-label="App Store" rel="noreferrer" className="app-link__apple" target="_blank" href={apple_store} tabIndex={'0'}>
                                    <picture>
                                        <source
                                            className="lazyloading"
                                            srcSet={`${URL_IMAGES_I}/store_app-1x.png 1x, ${URL_IMAGES_I}/store_app-2x.png 2x`}
                                            width="160"
                                            height="44"
                                        />
                                        <img
                                            className="ls-is-cached lazyloaded"
                                            alt="App Store"
                                            width="160"
                                            height="44"
                                            src={`${URL_IMAGES_I}/store_app-1x.png`}
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </picture>
                                </a>
                                <a aria-label="Google Play" rel="noreferrer" className="app-link__google" target="_blank" href={google_play} tabIndex={'0'}>
                                    <picture>
                                        <source
                                            className=" lazyloading"
                                            srcSet={`${URL_IMAGES_I}/store_google-1x.png 1x, ${URL_IMAGES_I}/store_google-2x.png 2x`}
                                            width="160"
                                            height="44"
                                        />
                                        <img
                                            className=" ls-is-cached lazyloaded"
                                            alt="Google Play"
                                            width="160"
                                            height="44"
                                            src={`${URL_IMAGES_I}/store_google-1x.png`}
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </picture>
                                </a>
                                {!isRequest && (
                                    <>
                                        <a
                                            aria-label="AppGallery"
                                            rel="noreferrer"
                                            className="app-link__huawei"
                                            target="_blank"
                                            href={huawei_store}
                                            tabIndex={'0'}
                                        >
                                            <picture>
                                                <source
                                                    className=" lazyloading"
                                                    srcSet={`${URL_IMAGES_I}/store_huawei-1x.png 1x, ${URL_IMAGES_I}/store_huawei@2x.png 2x`}
                                                    width="160"
                                                    height="44"
                                                />
                                                <img
                                                    className=" ls-is-cached lazyloaded"
                                                    alt="Huawei Play"
                                                    width="160"
                                                    height="44"
                                                    src={`${URL_IMAGES_I}/store_huawei-1x.png`}
                                                    loading="lazy"
                                                    decoding="async"
                                                />
                                            </picture>
                                        </a>
                                        <div className="qr-code__block">
                                            <a
                                                className="maincopy color-GT ce ajaxLinkDecorated"
                                                style={{ cursor: 'pointer' }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    popupRef.current?.open(<QRPopup popupRef={popupRef} />);
                                                }}
                                                onKeyPress={(e) => {
                                                    e.preventDefault();
                                                    popupRef.current?.open(<QRPopup popupRef={popupRef} />);
                                                }}
                                                id="qr-code"
                                                data-fragments="popup-body"
                                                tabIndex={'0'}
                                            >
                                                {mobileNameUrl}
                                            </a>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <PromoPopup ref={popupRef} />
        </>
    );
};

MobileApp.propTypes = {
    data: PropTypes.object,
};

export default MobileApp;
